import React from "react";
import "./VideoPlayer.css";
import { Icon } from "../Icons/Icon";
import { ChapterContent, ContentType } from "../Types/CourseTypes";

interface VideoPlayerProps {
  content: ChapterContent;
  onCompleted?: () => void;
}

interface VideoSeekBarStyles extends React.CSSProperties {
  "--played"?: string;
  "--buffered"?: string;
  "--volume"?: string;
}

export const VideoPlayer = ({ content, onCompleted }: VideoPlayerProps) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [volume, setVolume] = React.useState(1);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [buffered, setBuffered] = React.useState(0);
  const [hasCompleted, setHasCompleted] = React.useState(false);

  const video = content.video;

  /* ==============================
     FULLSCREEN STATE
  ============================== */
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  /* ==============================
     GUARD
  ============================== */
  if (!video || content.contentType !== ContentType.Video) {
    return (
      <div className="videoPlayerRoot videoPlayerNoVideo">
        <p>Ingen video att visa.</p>
      </div>
    );
  }

  /* ==============================
     CONTROLS
  ============================== */
  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      return;
    }

    void videoRef.current.play();
  };

  const handleProgress = () => {
    const v = videoRef.current;
    if (!v || !v.buffered.length || !Number.isFinite(v.duration) || v.duration <= 0) return;

    const end = v.buffered.end(v.buffered.length - 1);
    setBuffered(Math.min((end / v.duration) * 100, 100));
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;

    setCurrentTime(v.currentTime);

    if (!hasCompleted && Number.isFinite(v.duration) && v.duration > 0) {
      const percent = (v.currentTime / v.duration) * 100;
      if (percent >= 90) {
        setHasCompleted(true);
        onCompleted?.();
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = Number(e.target.value);
    setVolume(vol);
    if (videoRef.current) videoRef.current.volume = vol;
  };

  const toggleFullscreen = () => {
    const container = videoRef.current?.parentElement;
    if (!container) return;

    if (!document.fullscreenElement) container.requestFullscreen();
    else document.exitFullscreen();
  };

  const formatTime = (time: number) => {
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return `${min}:${sec.toString().padStart(2, "0")}`;
  };

  const playedPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration > 0 ? buffered : 0;
  const volumePercent = volume * 100;

  /* ==============================
     RENDER
  ============================== */
  return (
    <div className="videoPlayerRoot">
      <div className="videoPlayerBody">
        <video
          ref={videoRef}
          src={video.url}
          className="videoPlayerVideo"
          onTimeUpdate={handleTimeUpdate}
          onProgress={handleProgress}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />

        <div className="videoPlayerControlsOverlay">
          {/* SEEK BAR */}
          <div className="videoPlayerSeekWrapper">
            <input
              type="range"
              min={0}
              max={duration}
              value={currentTime}
              onChange={handleSeek}
              className="videoPlayerSeekBar"
              aria-label="Sök i videon"
              style={
                {
                  "--played": `${playedPercent}%`,
                  "--buffered": `${bufferedPercent}%`
                } as VideoSeekBarStyles
              }
            />

            <div className="videoPlayerTime">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* CONTROLS */}
          <div className="videoPlayerControlsRow">
            <button
              type="button"
              onClick={togglePlay}
              className="videoPlayerPlayButton"
              aria-label={isPlaying ? "Pausa video" : "Spela video"}
            >
              {isPlaying ? <Icon.Pause width={20} height={20} fill="currentColor" /> : <Icon.Play width={20} height={20} fill="currentColor" />}
            </button>

            <div className="videoPlayerVolumeWrapper">
              <Icon.Volume width={18} height={18} fill="currentColor" />
              <input
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={volume}
                onChange={handleVolumeChange}
                className="videoPlayerVolumeSlider"
                aria-label="Volym"
                style={
                  { "--volume": `${volumePercent}%` } as React.CSSProperties
                }
              />
            </div>

            <button
              type="button"
              onClick={toggleFullscreen}
              className="videoPlayerFullscreenButton"
              aria-label={isFullscreen ? "Minimera video" : "Expandera video"}
            >
              {isFullscreen ? <Icon.Minimize width={20} height={20} fill="currentColor" /> : <Icon.Maximize width={20} height={20} fill="currentColor" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
